/**
 * Helpers for the heat pump history (GET /history/heat_pump/:port_id): the
 * time window of each scale, period totals and chart series.
 *
 * Idle and cooling electricity is shown together with space heating: the UI
 * splits only "heating" and "hot water" (docs/heat-pump-history.md, 4.7).
 */
import { DateTime } from 'luxon'
import { TimeString } from '../types/models'

export type HeatPumpScale = 'minute' | 'quarter' | 'hour' | 'day' | 'week' | 'month' | 'year'

export const HEAT_PUMP_SCALES: HeatPumpScale[] = ['minute', 'quarter', 'hour', 'day', 'week', 'month', 'year']

/** One time slice of the heat pump history API. Energies kWh, costs EUR, temperatures degC. */
export type HeatPumpHistoryRow = {
  timestamp: TimeString
  elec_space: number
  elec_dhw: number
  elec_cool: number
  elec_idle: number
  aux_space: number
  aux_dhw: number
  heat_space: number
  heat_dhw: number
  cool: number
  cop: number | null
  cost_space: number
  cost_dhw: number
  /** Cooling and idle. */
  cost_other: number
  /** The cooling part of `cost_other`. */
  cost_cool: number
  /** Margin and transfer fee inside the matching cost; own solar has none. */
  cost_fees_space: number
  cost_fees_dhw: number
  cost_fees_other: number
  cost_fees_cool: number
  priced: boolean
  elec_power_max: number | null
  t_outdoor: number | null
  t_flow: number | null
  t_return: number | null
  t_dhw_tank: number | null
  compressor_hours: number
  compressor_starts: number
  defrost_minutes: number
  controlled_hours: number
  /** Growth of the firmware's and the heat pump's own cumulative counters (kWh), resets skipped. */
  counter_elec: number
  machine_elec: number
  machine_heat: number
  samples: number
  coverage: number
}

/** Below this coverage a slice is drawn as incomplete. */
export const HEAT_PUMP_INCOMPLETE = 0.9

const TZ = 'Europe/Helsinki'

/** Length of the window each scale shows, and the step the API is asked for. */
const WINDOWS: Record<HeatPumpScale, { unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years', count: number }> = {
  // As the minute view of the meter and battery history: one hour.
  minute: { unit: 'minutes', count: 60 },
  quarter: { unit: 'hours', count: 24 },
  hour: { unit: 'hours', count: 48 },
  day: { unit: 'days', count: 31 },
  week: { unit: 'weeks', count: 12 },
  month: { unit: 'months', count: 12 },
  year: { unit: 'years', count: 5 },
}

const STARTS: Record<HeatPumpScale, 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'> = {
  minute: 'minute',
  quarter: 'hour',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
}

/**
 * The [from, to) window of a scale, `page` windows back from the present
 * (page 0 ends at the end of the current slice).
 */
export function heatPumpWindow(scale: HeatPumpScale, page: number, now: DateTime = DateTime.now()): [DateTime, DateTime] {
  const { unit, count } = WINDOWS[scale]
  const to = now.setZone(TZ).startOf(STARTS[scale]).plus({ [STARTS[scale]]: 1 }).minus({ [unit]: count * page })
  return [to.minus({ [unit]: count }), to]
}

const STEPS: Record<HeatPumpScale, object> = {
  minute: { minutes: 1 },
  quarter: { minutes: 15 },
  hour: { hours: 1 },
  day: { days: 1 },
  week: { weeks: 1 },
  month: { months: 1 },
  year: { years: 1 },
}

/** Start of every slice in [from, to), so that slices without data show as gaps. */
export function heatPumpSlots(scale: HeatPumpScale, from: DateTime, to: DateTime): DateTime[] {
  const slots: DateTime[] = []
  for (let t = from; t < to; t = t.plus(STEPS[scale])) {
    slots.push(t)
  }
  return slots
}

/** The API rows placed on the slots: `null` where a slot has no data. */
export function heatPumpOnSlots(rows: HeatPumpHistoryRow[], slots: DateTime[]): (HeatPumpHistoryRow | null)[] {
  const byMs = new Map(rows.map(r => [DateTime.fromISO(r.timestamp).toMillis(), r]))
  return slots.map(s => byMs.get(s.toMillis()) ?? null)
}

/**
 * Index of the latest slot the midpoint line may select: the last one holding
 * a completed minute. The minute in progress has no data yet (rows are written
 * when a minute ends), so on the minute scale that is the previous minute, on
 * the others the current slice unless it began this very minute (Joni,
 * 29.10.2026: the line could go past the present onto a slice reading zero).
 * -1 when no slot qualifies.
 */
export function heatPumpLatestSlice(slots: DateTime[], now: DateTime = DateTime.now()): number {
  const minute = now.startOf('minute').toMillis()
  let i = slots.length - 1
  while (i >= 0 && slots[i].toMillis() >= minute) i--
  return i
}

/**
 * The [from, to) window a scale shows when it is opened: ending at the end of
 * the current slice, or centred on `anchor` (the moment in view before a scale
 * switch) but never past the current slice.
 */
export function heatPumpWindowAround(scale: HeatPumpScale, anchor: DateTime | null, now: DateTime = DateTime.now()): [DateTime, DateTime] {
  const { unit, count } = WINDOWS[scale]
  const latest = heatPumpWindow(scale, 0, now)[1]
  if (anchor === null) {
    return [latest.minus({ [unit]: count }), latest]
  }
  const centred = anchor.setZone(TZ).plus({ [unit]: Math.floor(count / 2) }).startOf(STARTS[scale])
  const to = centred < latest ? centred : latest
  return [to.minus({ [unit]: count }), to]
}

/**
 * The moment a scale switch centres the new window on: the middle of the view,
 * or null (the latest window) when the view reaches the current slice. A user
 * who has not panned into the past keeps seeing today: in prod (29.10.2026) a
 * switch from Pv to 15 min centred the new window on the middle of the 31 days
 * in view, two weeks back, and the heat pump's first hours of data were never
 * fetched.
 */
export function heatPumpSwitchAnchor(
  scale: HeatPumpScale, slots: DateTime[], view: { min: number, max: number } | null, now: DateTime = DateTime.now()
): DateTime | null {
  if (!view || slots.length === 0) return null
  const last = slots[Math.min(view.max, slots.length - 1)]
  if (!last || last.plus(STEPS[scale]) >= heatPumpWindow(scale, 0, now)[1]) return null
  return slots[Math.round((view.min + view.max) / 2)] ?? null
}

/**
 * Whether the value at `i` has no value next to it on either side: a line
 * cannot be drawn through it, so the chart shows it as a dot (a day with the
 * first readings of a heat pump, the only hour with data so far).
 */
export function heatPumpIsolated(values: (number | null)[], i: number): boolean {
  return values[i] != null && values[i - 1] == null && values[i + 1] == null
}

/** Whether a slice with data used no electricity: drawn as a mark on the baseline, as no bar would show. */
export function heatPumpNoElectricity(row: HeatPumpHistoryRow | null): boolean {
  if (!row) return false
  const s = heatPumpSplit(row)
  return s.elecSpace + s.elecDhw < 0.0005
}

/** Start of the page of older data before `from`: one window back. */
export function heatPumpPageBefore(scale: HeatPumpScale, from: DateTime): DateTime {
  const { unit, count } = WINDOWS[scale]
  return from.minus({ [unit]: count })
}

/**
 * End of the page of newer data after `to`: one window on, but never past the
 * end of the current slice. Null when `to` is already there. A window opened
 * around a moment in the past is extended with these until it reaches today.
 */
export function heatPumpPageAfter(scale: HeatPumpScale, to: DateTime, now: DateTime = DateTime.now()): DateTime | null {
  const { unit, count } = WINDOWS[scale]
  const latest = heatPumpWindow(scale, 0, now)[1]
  if (to >= latest) return null
  const next = to.plus({ [unit]: count })
  return next < latest ? next : latest
}

const NUMBERS: (keyof HeatPumpHistoryRow)[] = [
  'elec_space', 'elec_dhw', 'elec_cool', 'elec_idle', 'aux_space', 'aux_dhw', 'heat_space', 'heat_dhw', 'cool', 'cop',
  'cost_space', 'cost_dhw', 'cost_other', 'cost_cool', 'cost_fees_space', 'cost_fees_dhw', 'cost_fees_other',
  'cost_fees_cool', 'elec_power_max', 't_outdoor', 't_flow', 't_return', 't_dhw_tank',
  'compressor_hours', 'compressor_starts', 'defrost_minutes', 'controlled_hours', 'counter_elec', 'machine_elec',
  'machine_heat', 'samples', 'coverage',
]
const NULLABLE: (keyof HeatPumpHistoryRow)[] = ['cop', 'elec_power_max', 't_outdoor', 't_flow', 't_return', 't_dhw_tank']

/**
 * The rows of an API answer that can be drawn. Anything else than a list of rows
 * (an empty body, an HTML page from a proxy during a deploy) is no rows; a
 * missing or non-numeric number is 0, or null where the API may send null, so
 * that formatting a row never throws.
 */
export function heatPumpRows(data: unknown): HeatPumpHistoryRow[] {
  if (!Array.isArray(data)) return []
  return data
    .filter((r): r is Record<string, unknown> =>
      typeof r === 'object' && r !== null && typeof r.timestamp === 'string' && DateTime.fromISO(r.timestamp).isValid)
    .map(r => {
      const row: Record<string, unknown> = { ...r, priced: r.priced === true }
      for (const key of NUMBERS) {
        const value = row[key]
        if (typeof value !== 'number' || !Number.isFinite(value)) row[key] = NULLABLE.includes(key) ? null : 0
      }
      return row as HeatPumpHistoryRow
    })
}

/** Electricity and cost of a slice, split the way the UI shows it. */
export function heatPumpSplit(row: HeatPumpHistoryRow) {
  return {
    elecSpace: row.elec_space + row.elec_idle + row.elec_cool,
    elecDhw: row.elec_dhw,
    heatSpace: row.heat_space,
    heatDhw: row.heat_dhw,
    costSpace: row.cost_space + row.cost_other,
    costDhw: row.cost_dhw,
    aux: row.aux_space + row.aux_dhw,
  }
}

export type HeatPumpTotals = ReturnType<typeof heatPumpSplit> & {
  elec: number
  heat: number
  cost: number
  cop: number | null
  copSpace: number | null
  copDhw: number | null
  priced: boolean
}

function ratio(a: number, b: number): number | null {
  return b > 0.01 ? a / b : null
}

/** Totals over a list of slices. */
export function heatPumpTotals(rows: HeatPumpHistoryRow[]): HeatPumpTotals {
  const sum = rows.map(heatPumpSplit).reduce((acc, s) => ({
    elecSpace: acc.elecSpace + s.elecSpace,
    elecDhw: acc.elecDhw + s.elecDhw,
    heatSpace: acc.heatSpace + s.heatSpace,
    heatDhw: acc.heatDhw + s.heatDhw,
    costSpace: acc.costSpace + s.costSpace,
    costDhw: acc.costDhw + s.costDhw,
    aux: acc.aux + s.aux,
  }), { elecSpace: 0, elecDhw: 0, heatSpace: 0, heatDhw: 0, costSpace: 0, costDhw: 0, aux: 0 })

  const elec = sum.elecSpace + sum.elecDhw
  const heat = sum.heatSpace + sum.heatDhw

  return {
    ...sum,
    elec,
    heat,
    cost: sum.costSpace + sum.costDhw,
    cop: ratio(heat, elec),
    copSpace: ratio(sum.heatSpace, sum.elecSpace),
    copDhw: ratio(sum.heatDhw, sum.elecDhw),
    priced: rows.every(r => r.priced),
  }
}

/**
 * Value of an energy per slice as the chart draws it: kWh, or the average kW on
 * the minute, quarter and hour scales (energy / hours of the slice).
 */
export function heatPumpChartValue(kwh: number, scale: HeatPumpScale): number {
  switch (scale) {
    case 'minute': return kwh * 60
    case 'quarter': return kwh * 4
    default: return kwh
  }
}

/** Whether the energy chart shows power (kW) instead of energy (kWh). */
export function heatPumpShowsPower(scale: HeatPumpScale): boolean {
  return scale === 'minute' || scale === 'quarter' || scale === 'hour'
}

/** The row as the History page's fee switch has it: without fees, the fees are taken out of each cost. */
export function heatPumpWithFees(row: HeatPumpHistoryRow, includeFees: boolean): HeatPumpHistoryRow {
  if (includeFees) return row
  return {
    ...row,
    cost_space: row.cost_space - row.cost_fees_space,
    cost_dhw: row.cost_dhw - row.cost_fees_dhw,
    cost_other: row.cost_other - row.cost_fees_other,
    cost_cool: row.cost_cool - row.cost_fees_cool,
  }
}

/** What a slice's electricity cost per kWh of electricity, c/kWh, or null. */
export function heatPumpElectricityPrice(row: HeatPumpHistoryRow): number | null {
  const s = heatPumpSplit(row)
  const elec = s.elecSpace + s.elecDhw
  return elec > 0.01 && row.priced ? (s.costSpace + s.costDhw) / elec * 100 : null
}

/**
 * What a slice's heat cost per kWh of heat, c/kWh, or null: the heat includes the backup heater. Cooling is left out
 * of the cost, as it produces no heat; idle electricity stays in, as it is what keeping the heat pump ready costs.
 */
export function heatPumpHeatPrice(row: HeatPumpHistoryRow): number | null {
  const s = heatPumpSplit(row)
  const heat = s.heatSpace + s.heatDhw
  return heat > 0.01 && row.priced ? (s.costSpace + s.costDhw - row.cost_cool) / heat * 100 : null
}
